// src/components/AppLayout.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserCircle, Home as HomeIcon, Users, Book, 
  TrendingUp, Settings, LogOut, BookOpen, Clipboard 
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { useUser } from '../contexts/UserContext';
import { useTheme } from '../contexts/ThemeContext'; // Keep theme support
import { getColorScheme } from '../utils/colorSchemes'; // Import the shared utility


const AppLayout = ({ children, activeTab = 'home' }) => {
  const { userData, logout } = useUser();
  const { showToast } = useToast();
  const { theme } = useTheme(); // Keep theme integration
  const navigate = useNavigate();
  const currentTab = activeTab;

  // Get the current color scheme based on user's accent color preference
const { accentColor } = useTheme();
const currentAccentColor = accentColor || userData?.accentColor || 'maroon';
const colors = getColorScheme(currentAccentColor, theme);

  // Different menu items based on role
  const studentMenuItems = [
    { icon: HomeIcon, label: 'Home', id: 'home' },
    { icon: Users, label: 'Mentor', id: 'mentor' },
    { icon: TrendingUp, label: 'Progress', id: 'progress' },
    { icon: Book, label: 'Resources', id: 'resources' },
    { icon: Settings, label: 'Settings', id: 'settings' }
  ];

  const mentorMenuItems = [
    { icon: HomeIcon, label: 'Home', id: 'home' },
    { icon: Users, label: 'Mentor', id: 'mentor' },
    { icon: BookOpen, label: 'Resources', id: 'resources' },
    { icon: Settings, label: 'Settings', id: 'settings' }
  ];

  const menuItems = userData.role === 'mentor' ? mentorMenuItems : studentMenuItems;

  const handleProfileClick = () => {
    navigate('/app/profile');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      showToast('Sign out failed. Please try again.', 'error');
    }
  };

  const selectTab = (tabId) => {
    switch (tabId) {
      case 'home':
        navigate('/app');
        break;
      case 'mentor':
        navigate('/app/mentor');
        break;
      case 'settings':
        navigate('/app/settings');
        break;
      case 'progress':
        navigate('/app/progress');
        break;
      case 'resources':
        navigate('/app/resources');
        break;
      default:
        break;
    }
  };

  // Determine background based on theme
  const bgGradient = theme === 'light'
    ? 'bg-gradient-to-br from-[#fdf4ed] via-[#fef5ee] to-[#fdf4ed]' 
    : 'bg-gradient-to-br from-[#4a1825] via-[#2c0f16] to-[#4a1825]';

  // Use dynamic header color based on accent color
  const headerBg = theme === 'light' 
    ? colors.headerBg
    : colors.headerBgDark;
    
  // Determine header text color
  const headerTextColor = 'text-white';

  return (
    <div className={`min-h-screen ${bgGradient}`}>
      {/* Header with dynamic colors */}
      <header className="shadow-lg" style={{ backgroundColor: headerBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className={`text-xl font-bold ${headerTextColor}`}>Pathway2Practice</h1>
            </div>
            <div className="flex items-center space-x-2">
              <div
                className={`flex items-center space-x-2 cursor-pointer px-3 py-2 rounded-lg transition-all duration-200 ${headerTextColor}`}
                style={{
                  ':hover': {
                    backgroundColor: theme === 'light' ? colors.hoverBg : colors.hoverBgDark
                  }
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme === 'light' ? colors.hoverBg : colors.hoverBgDark;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
                onClick={handleProfileClick}
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-white" style={{ color: colors.primary }}>
                  {userData?.profileImage ? (
                    <img 
                      src={userData.profileImage} 
                      alt={userData.name}
                      className="w-8 h-8 rounded-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/api/placeholder/32/32";
                      }}
                    />
                  ) : (
                    <UserCircle className="w-6 h-6" />
                  )}
                </div>
                <span className={headerTextColor}>{userData?.name || 'User'}</span>
                {userData?.role === 'mentor' && (
                  <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: colors.navActive }}>
                    Mentor
                  </span>
                )}
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 px-3 py-2 bg-[#c53030] hover:bg-[#9b2c2c] text-white rounded-lg transition-colors duration-200"
                aria-label="Logout"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24">
        {children}
      </main>
      
      {/* Bottom Navigation Bar with dynamic colors */}
      <nav className={`fixed bottom-0 inset-x-0 z-10 backdrop-blur-sm shadow-lg border-t ${
        theme === 'light' 
          ? 'bg-white bg-opacity-90' 
          : 'bg-[#4a1825] bg-opacity-90'
        }`}
        style={{
          borderTopColor: theme === 'light' ? `${colors.primary}20` : `${colors.primary}50`
        }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-around">
            {menuItems.map((item) => {
              const isActive = currentTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => selectTab(item.id)}
                  className={`flex flex-col items-center py-3 px-4 transition-all duration-200 relative`}
                  style={{
                    color: isActive 
                      ? (theme === 'light' ? colors.navActive : '#f8e3dc')
                      : (theme === 'light' ? '#6b7280' : '#d1d5db')
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.target.style.color = theme === 'light' ? colors.navHover : 'white';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.target.style.color = theme === 'light' ? '#6b7280' : '#d1d5db';
                    }
                  }}
                  aria-label={item.label}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <item.icon className="h-6 w-6" />
                  <span className="text-xs mt-1">{item.label}</span>
                  {isActive && (
                    <span 
                      className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 rounded-t-full"
                      style={{
                        backgroundColor: theme === 'light' ? colors.navActive : '#f8e3dc'
                      }}
                    ></span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </nav>
    </div>
  );
};

export default AppLayout;